import React from "react";

import { ListGroup, ListRow } from "@lattice/frontend/components/kit/List";
import { Pill } from "@lattice/frontend/components/kit/Pill";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import { SettingsPanelError, SettingsPanelLoading } from "./SettingsPanelState";

export type T_NotificationPreferenceKind = "calendar" | "event" | "price";

const NOTIFICATION_ROWS: ReadonlyArray<{
  kind: T_NotificationPreferenceKind;
  subtitle: string;
  title: string;
}> = [
  {
    kind: "calendar",
    subtitle: "Earnings and economic events you've added",
    title: "Calendar alerts",
  },
  {
    kind: "event",
    subtitle: "World events you asked Prism to watch",
    title: "Event alerts",
  },
  {
    kind: "price",
    subtitle: "Price targets on stocks you follow",
    title: "Price alerts",
  },
];

export type T_NotificationsPanelViewProps = {
  error: string | null;
  onToggle: (kind: T_NotificationPreferenceKind, enabled: boolean) => void;
  pendingKind: T_NotificationPreferenceKind | null;
  values: Record<T_NotificationPreferenceKind, boolean>;
};

export function NotificationsPanelView({
  error,
  onToggle,
  pendingKind,
  values,
}: T_NotificationsPanelViewProps) {
  return (
    <div className="shell-settings-form">
      <p className="shell-settings__body">
        These preferences apply to alert delivery across your registered
        devices.
      </p>
      <ListGroup>
        {NOTIFICATION_ROWS.map((row) => {
          const enabled = values[row.kind];
          return (
            <ListRow
              key={row.kind}
              subtitle={row.subtitle}
              title={row.title}
              trailing={
                <Pill
                  ariaLabel={`${row.title}: ${enabled ? "on" : "off"}`}
                  disabled={pendingKind !== null}
                  label={
                    pendingKind === row.kind
                      ? "Saving…"
                      : enabled
                        ? "On"
                        : "Off"
                  }
                  onClick={() => onToggle(row.kind, !enabled)}
                  selected={enabled}
                />
              }
            />
          );
        })}
      </ListGroup>
      {error ? <SettingsPanelError message={error} /> : null}
    </div>
  );
}

export function NotificationsPanel() {
  const utils = trpcApi.useUtils();
  const preferences = trpcApi.notification_prefs.get_preferences.useQuery(
    undefined,
    { refetchOnWindowFocus: false, staleTime: 60_000 },
  );
  const setPreference = trpcApi.notification_prefs.set_preference.useMutation({
    onSuccess: async () => {
      await utils.notification_prefs.get_preferences.invalidate();
    },
  });

  if (preferences.isPending) {
    return <SettingsPanelLoading label="Loading notification preferences" />;
  }
  if (preferences.isError || !preferences.data) {
    return (
      <SettingsPanelError message="Notification preferences could not be loaded." />
    );
  }

  const data = preferences.data.data;
  return (
    <NotificationsPanelView
      error={setPreference.error?.message ?? null}
      onToggle={(kind, enabled) => setPreference.mutate({ enabled, kind })}
      pendingKind={
        setPreference.isPending ? (setPreference.variables?.kind ?? null) : null
      }
      values={{
        calendar: data.calendar_alerts,
        event: data.event_alerts,
        price: data.price_alerts,
      }}
    />
  );
}
